<template>
  <div class="alarm-tab-layout d-flex ga-4 flex-grow-1 min-h-0 h-100">
    <aside class="alarm-tab-sidebar gm-card flex-shrink-0 rounded-xl">
      <v-tabs
        v-model="activeTab"
        class="alarm-tab-nav"
        direction="vertical"
        color="var(--color-primary)"
        density="comfortable"
        hide-slider
      >
        <v-tab v-for="item in visibleTabs" :key="item.value" :value="item.value" :prepend-icon="item.icon">
          {{ item.label }}
        </v-tab>
      </v-tabs>
    </aside>

    <div
      class="alarm-tab-panel gm-card flex-grow-1 min-h-0 d-flex flex-column overflow-auto rounded-xl"
      :class="{ 'alarm-tab-panel--logs': activeTab === 'logs' }"
    >
      <component
        v-if="currentComponent"
        :is="currentComponent"
        class="alarm-tab-panel__body flex-grow-1 min-h-0 d-flex flex-column"
      />
      <div v-else class="alarm-tab-empty">暂无可访问的告警管理页面</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { Activity, CogIcon, Code2, ScrollText } from 'lucide-vue-next'
import useUserStore from '@/store/modules/user'

// Pages
import AlarmDashboard from './AlarmDashboard/index.vue'
import AlarmConfig from './AlarmConfig/index.vue'
import AlarmCodes from './AlarmCodes/index.vue'
import AlarmLog from './AlarmLog/index.vue'

const Components = {
  dashboard: AlarmDashboard,
  config: AlarmConfig,
  codes: AlarmCodes,
  logs: AlarmLog,
}
const tabs = [
  { value: 'logs', label: '告警日志', icon: ScrollText, perms: ['ops:alarmLog:query'] },
  { value: 'dashboard', label: '告警看板', icon: Activity, perms: ['ops:alarmDashboard:query'], hidden: true },
  { value: 'config', label: '告警规则', icon: CogIcon, perms: ['ops:alarmConfig:query'] },
  { value: 'codes', label: '告警编码', icon: Code2, perms: ['ops:alarmCode:query'] },
]
const userStore = useUserStore()
const { permissions } = storeToRefs(userStore)

function hasPerms(perms) {
  if (!Array.isArray(perms) || !perms.length) return true
  return (permissions.value || []).some(permission => permission === '*:*:*' || perms.includes(permission))
}

const visibleTabs = computed(() => tabs.filter(item => !item.hidden && hasPerms(item.perms)))

const activeTab = ref<'dashboard' | 'logs' | 'config' | 'codes'>('logs')
const currentComponent = computed(() => {
  if (!visibleTabs.value.some(item => item.value === activeTab.value)) return null
  return Components[activeTab.value]
})

watch(visibleTabs, (list) => {
  if (!list.length) return
  if (!list.some(item => item.value === activeTab.value)) {
    activeTab.value = list[0].value
  }
}, { immediate: true, flush: 'post' })
</script>

<style scoped lang="scss">
/* 与运维 summary 顶栏 tab-bar 一致的轻阴影，统一「卡片」层次 */
.alarm-tab-sidebar,
.alarm-tab-panel {
  box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
}

.alarm-tab-sidebar {
  width: 200px;
  padding: 10px 8px;
  align-self: stretch;
}

.alarm-tab-nav {
  :deep(.v-tab) {
    justify-content: flex-start;
    border-radius: 10px;
    min-height: 46px;
    margin-bottom: 4px;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
    font-size: 14px;
  }

  :deep(.v-tab--selected) {
    background: linear-gradient(
      90deg,
      color-mix(in srgb, var(--color-primary) 20%, transparent) 0%,
      color-mix(in srgb, var(--color-primary) 6%, transparent) 100%
    );
  }

  :deep(.v-tab:not(.v-tab--selected):hover) {
    background: rgba(8, 40, 87, 0.045);
  }

  :deep(.v-tab .v-btn__overlay) {
    border-radius: 10px;
  }
}

.alarm-tab-panel__body {
  min-width: 0;
}

.alarm-tab-panel--logs {
  overflow: hidden !important;
}
</style>
